<template>
  <b-card>
    <b-row>
      <b-col class="col-12 col-lg-4 col-xl-5">
        <p v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </p>
        <pos-pay-amount
          v-if="!busy"
          :organization="organization"
          :invoice="invoice"
          :show-convenience-fee-preview="showConvenienceFeePreview"
          @convenience-fee-price="convenience_fee_price = $event"
        ></pos-pay-amount>
      </b-col>
      <b-col class="col-12 col-lg-8 col-xl-7">
        <b-form-group v-if="showPaymentChoices" :label="trans('pay_with')">
          <b-form-radio-group v-model="show" :disabled="busy" @change="checkPayment">
            <b-form-radio v-if="canShowStripe" value="stripe">
              {{ trans('credit_card') }}
            </b-form-radio>
            <b-form-radio v-if="canShowInterac" value="interac">
              {{ trans('interac_e_transfer') }}
            </b-form-radio>
            <b-form-radio v-if="canShowOther" value="other">
              {{ invoice.allow_other_payment_method_label || trans('other') }}
            </b-form-radio>
            <b-form-radio v-if="canShowGiftCard" value="gift_card">
              {{ trans('gift_card') }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <div v-if="invoice.pay_now_amount > 0" :style="`display: ${!invoice.must_pay_interac && show == 'stripe' ? 'block' : 'none'};`">
          <p v-if="showAdminOptions" class="text-right">
            <test-mode-switch
              v-model="organization.is_test_mode"
              :url="`/organization/${organization.id}/account/set_test_mode`"
              :title="trans(organization.is_test_mode ? 'test_mode_about' : 'not_test_mode_about')"
              disabled
            />
          </p>
          <b-form-group>
            <label>{{ trans('name_on_card') }}</label>
            <b-input v-model="name_on_card" required />
          </b-form-group>
          <b-form-group>
            <label>{{ trans('credit_card') }}</label>
            <div ref="card"></div>
          </b-form-group>
        </div>

        <div v-if="invoice.pay_now_amount > 0 && canShowInterac && show == 'interac'" class="mt-2 mb-4">
          <p v-if="!invoice.interac">
            <i class="fa fa-fw fa-circle-notch fa-spin"></i>
            {{ trans('please_wait') }}
          </p>
          <div v-if="!!invoice.interac">
            <h5>{{ trans('interac_e_transfer') }}</h5>
            <interac-instructions
              :organization="organization"
              :interac="invoice.interac"
              class="mx-4"
            ></interac-instructions>
          </div>
        </div>

        <div v-if="invoice.pay_now_amount > 0 && canShowOther && show == 'other'" class="mt-2 mb-4">
          <h5>{{ invoice.allow_other_payment_method_label || trans('other') }}</h5>
          <div v-html="invoice.allow_other_payment_method_description || trans('allow_other_payment_method_description')"></div>
          <b-form-group>
            <b-textarea v-model="other_payment_client_note" :disabled="busy"></b-textarea>
          </b-form-group>
        </div>

        <pos-gift-cards
          v-if="canShowGiftCard && show == 'gift_card'"
          class="mt-2 mb-4"
          ref="checkout_gift_cards"
          :organization="organization"
          :invoice="invoice"
          @invoice="onGiftCardUpdated"
          @busy="busy = $event"
          @error="error = $event"
        ></pos-gift-cards>

        <p v-if="invoice.pay_now_amount == 0" class="text-success text-center lead m-4">
          <i class="fa fa-fw fa-smile"></i>
          {{ trans('no_payment_required_today') }}
        </p>

        <b-checkbox v-model="accept_terms" :value="true" :unchecked-value="false" class="my-3">
          {{ trans('checkout_accept_terms') }}
          <a :href="`${iframeUrlPrefix}/org/${organization.id}/terms`" target="_blank">
            <i class="fa fa-fw fa-external-link-alt"></i>
          </a>
        </b-checkbox>
        <p v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </p>
        <p v-if="error" class="text-danger">
          {{ error }}
        </p>

        <b-button v-if="invoice.pay_now_amount > 0 && !invoice.must_pay_interac && show == 'stripe'" block variant="branded" class="my-3" :disabled="busy || !name_on_card || !accept_terms" @click="onPayStripe">
          {{ trans('pay_now') }}
        </b-button>
        <b-button v-if="invoice.pay_now_amount > 0 && canShowInterac && show == 'interac'" block variant="branded" class="my-3" :disabled="busy || !accept_terms" @click="onPaidInterac">
          {{ trans('i_paid_interac') }}
        </b-button>
        <b-button v-if="invoice.pay_now_amount > 0 && canShowOther && show == 'other'" block variant="branded" class="my-3" :disabled="busy || !accept_terms" @click="onPaidOther">
          {{ trans('i_confirm') }}
        </b-button>
        <b-button v-if="canShowGiftCard && show == 'gift_card'" block variant="branded" class="my-3" :disabled="busy || !accept_terms" @click="onApplyGiftCard">
          {{ trans('apply') }}
        </b-button>
        <b-button v-if="invoice.pay_now_amount == 0" block variant="branded" class="my-3" :disabled="busy || !accept_terms" @click="onReserve">
          {{ trans('reserve_now') }}
        </b-button>

        <div v-if="invoice.total > 0 && showAdminOptions">
          <br/>
          <admin-options
            :organization="organization"
            :invoice="invoice"
            :show-convenience-fee-preview="showConvenienceFeePreview"
            :disabled="busy"
            :client-secret="client_secret"
            @client-secret="client_secret = $event"
            @invoice="$emit('invoice', $event)"
            @next="$emit('next')"
            @checkPayment="checkPayment"
            :isCart="false"
          ></admin-options>
        </div>
      </b-col>
    </b-row>

    <b-row class="my-2">
      <b-col>
        <b-button variant="outline-secondary" class="d-none d-lg-block" :disabled="busy" @click="$emit('previous')">
          {{ trans('previous') }}
        </b-button>
        <b-button variant="outline-secondary" block class="d-lg-none" :disabled="busy" @click="$emit('previous')">
          {{ trans('previous') }}
        </b-button>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
  import Dollar from './../billing/Dollar'
  import TestModeSwitch from './../../../common/components/TestModeSwitch'
  import AdminOptions from './CheckoutAdminOptions'
  import InteracInstructions from './../interac/InteracInstructions'
  import PosPayAmount from './../pos/PosPayAmount'
  import PosGiftCards from './../pos/PosGiftCards'

  let stripe = undefined
  let elements = undefined
  let card = undefined
  let style = {
    base: {
      fontSize: '16px',
      lineHeight: '24px',
      color: "#000",
    },

    invalid: {
      // All of the error styles go inside of here.
    }
  }

  export default {
    props: {
      organization: Object,
      invoice: Object,
      recipient: Object,
    },
    components: {
      Dollar,
      TestModeSwitch,
      AdminOptions,
      InteracInstructions,
      PosPayAmount,
      PosGiftCards,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        iframeUrlPrefix: _.iframeUrlPrefix,
        busy: false,
        error: null,
        // Payment
        stripe_public_key: null,
        client_secret: null,
        stripe_account_id: null,
        card: null,
        name_on_card: null,
        accept_terms: true,
        show: this.invoice.must_pay_interac ? 'interac' : 'stripe',
        other_payment_client_note: null,
        convenience_fee_price: null,
      }
    },
    computed: {
      showConvenienceFeePreview() {
        return this.organization.has_convenience_fee && this.show == 'stripe'
      },
      showAdminOptions() {
        return this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.has_admin_payments &&
          !!this.invoice.admin
      },
      canShowStripe() {
        // When Stripe is not configured, pay_now_amount is always 0
        // It is not possible to pay any other way than offline!
        return this.invoice.pay_now_amount > 0 && !this.invoice.must_pay_interac
      },
      canShowInterac() {
        return this.invoice.pay_now_amount > 0 && (this.invoice.must_pay_interac || this.invoice.can_also_pay_interac)
      },
      canShowOther() {
        return this.invoice.pay_now_amount > 0 && this.invoice.allow_other_payment_method
      },
      canShowGiftCard() {
        if (this.invoice.applied_gift_card_amount > 0) return true
        return this.invoice.pay_now_amount > 0 &&
          this.organization.has_gift_cards &&
          !this.invoice.prevent_paying_with_gift_cards &&
          !this.invoice.has_gift_card_purchase
      },
      showPaymentChoices() {
        return this.invoice.can_also_pay_interac || this.canShowOther || this.canShowGiftCard
      },
    },
    watch: {
      busy() {
        this.$emit('busy', this.busy)
      },
    },
    methods: {
      prepareStripePayment() {
		    if (this.invoice.pay_now_amount == 0) return
        this.busy = true
        this.error = false
        axios.post(`/org/${this.organization.id}/cart/${this.invoice.can_also_pay_interac ? 'prepare_stripe_and_interac_payment' : 'prepare_stripe_payment'}`, {amount: this.invoice.pay_now_amount})
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data.invoice)
          this.stripe_public_key = response.data.stripe_info.public_key
          this.client_secret = response.data.stripe_info.client_secret
          this.stripe_account_id = response.data.stripe_info.account_id
          setTimeout(this.onReady.bind(this), 100)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onReady() {
        stripe = Stripe(this.stripe_public_key, {
          stripeAccount: this.stripe_account_id
        })
        elements = stripe.elements()
        let data = {
          style: style,
          hidePostalCode: true,
        }
        /*if (this.recipient &&
          this.recipient.extra &&
          this.recipient.extra.account_owner &&
          this.recipient.extra.account_owner.zip_postal_code)
          data.value = {
            postalCode: this.recipient.extra.account_owner.zip_postal_code
          }*/
        card = elements.create('card', data)
        card.mount(this.$refs.card);
        this.card = card
      },
      onPayStripe() {
        this.busy = true
        this.error = null

        if (!this.name_on_card) {
          this.busy = false
          this.error = trans('name_on_card_missing')
          return
        }
        stripe.confirmCardPayment(this.client_secret, {
          payment_method: {
            card: card,
            billing_details: {name: this.name_on_card}
          }
        }).then(result => {
          if (result.error) {
            this.busy = false
            this.error = result.error.message
            return
          }
          else if (result.paymentIntent.status == 'succeeded') {
            this.onReserve()
          }
          else if (result.paymentIntent.status == 'requires_action') {
            // Dynamic 3D secure stuff
            stripe.handleNextAction({
              clientSecret: this.client_secret
            }).then(result => {
              if (result.error) {
                this.busy = false
                this.error = result.error.message
              } else {
                // Assume it worked
                this.onReserve()
              }
            })
          }
          else {
            this.busy = false
            this.error = 'Unexpected error'
          }
        });
      },
      prepareInteracPayment() {
        if (this.invoice.pay_now_amount  == 0) return
        this.busy = true
        this.error = false
        axios.post(`/org/${this.organization.id}/cart/prepare_interac_payment`, {amount: this.invoice.pay_now_amount})
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onPaidInterac() {
        this.busy = true
        this.error = null
        axios.post(`/org/${this.organization.id}/cart/confirmation?payment=interac`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$nextTick(() => {
            this.$emit('next')
          })
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onPaidOther() {
        this.busy = true
        this.error = null
        axios.post(`/org/${this.organization.id}/cart/confirmation?message=${encodeURIComponent(this.other_payment_client_note)}`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$nextTick(() => {
            this.$emit('next')
          })
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onGiftCardUpdated(invoice) {
        this.$emit('invoice', invoice)
        if (!!invoice.finalized_at) {
          this.$nextTick(() => {
            this.$emit('next')
          })
        }
      },
      onApplyGiftCard() {
        this.$refs.checkout_gift_cards.onApplyGiftCard()
      },
      onReserve(status) {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/confirmation`
        if (this.convenience_fee_price && this.convenience_fee_price > 0 && this.show == 'stripe') {
          url += '?payment=stripe'
        }
        axios.post(url)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$nextTick(() => {
            this.$emit('next')
          })
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      checkPayment() {
        if (this.show == 'stripe') {
          this.show_cc_fee_preview = true
        }
        else this.show_cc_fee_preview = false
      }
      
    },
    mounted() {
      window.paymentForm = this
      if (this.invoice.pay_now_amount > 0) {
        if (this.invoice.must_pay_interac)
        this.prepareInteracPayment()
      else
        this.prepareStripePayment()
      }
    },
  }
</script>